import { html } from '../lib/html.js';
import { busy, confirm, toast, toastError, toastSuccess } from '../lib/ui.js';
import { fmtKm } from '../services/maintenances.js';
import { plateBadge, serialBadge } from './vehicle-hero.js';
import { vehicles } from '../services/vehicles.js';
import { Page, go } from './base.js';

/**
 * /transferir/:token — o comprador abre o link (ou lê o QR) do vendedor.
 * Sem login: a guarda manda para entrar/criar conta e volta para cá.
 * Mostra o veículo, de quem vem, quantos registros de manutenção vêm junto e a km da venda.
 * Aceitar: o veículo entra na garagem como dono provisório (o vendedor sai). Recusar: o vendedor é avisado.
 */
class TransferAcceptPage extends Page {
  template() {
    return html`
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-title>Transferência de veículo</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <div class="narrow body wiz"></div>
      </ion-content>`;
  }

  get token() {
    if (this.tokenParam) return String(this.tokenParam);
    return decodeURIComponent(location.pathname.match(/\/transferir\/([^/?#]+)/)?.[1] ?? '');
  }

  set token(v) {
    this.tokenParam = v; // o ion-router passa :token como propriedade
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el?.dataset.action === 'accept') this.accept(el);
      if (el?.dataset.action === 'decline') this.decline();
      if (el?.dataset.action === 'home') go('/home', 'root');
    });
  }

  async onEnter() {
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      this.data = await vehicles.transferPreview(this.token);
      this.render();
    } catch (err) {
      this.renderClosed(err.status === 404 ? 'Este link de transferência não existe. Confira se copiou o link inteiro.' : err.message);
    }
  }

  renderClosed(message) {
    this.$('.body').innerHTML = html`
      <div class="wiz-icon"><ion-icon name="alert-circle-outline"></ion-icon></div>
      <h1 class="wiz-title">Link indisponível</h1>
      <p class="wiz-lead">${message}</p>
      <div class="wiz-foot"><ion-button expand="block" fill="outline" data-action="home">Ir para o início</ion-button></div>`.value;
  }

  render() {
    const { vehicle: v, seller, historyCount, transfer: t, mine } = this.data;
    if (t.status === 'EXPIRED') return this.renderClosed('Este link venceu. Peça um novo ao vendedor.');
    if (t.status === 'ACCEPTED') return this.renderClosed('Esta transferência já foi concluída.');
    if (t.status !== 'PENDING') return this.renderClosed('O vendedor cancelou este link. Peça um novo.');
    const model = [v?.brand, v?.model].filter(Boolean).join(' ');
    this.$('.body').innerHTML = html`
      <section class="hero">
        <div class="hero-top">${v?.plateFormatted ? plateBadge(v.plateFormatted) : v?.serialNumber ? serialBadge(v.serialNumber) : ''}</div>
        <div class="hero-title"><h1>${model || v?.plateFormatted || v?.serialNumber}</h1></div>
        <p class="hero-sub">${[v?.modelYear, v?.color].filter(Boolean).join(' · ')}</p>
      </section>
      ${mine
        ? html`<div class="panel notice"><span class="row-icon"><ion-icon name="information-circle-outline"></ion-icon></span><div><strong>Este é o seu link de venda</strong><p>Mostre o QR ou mande o link para o comprador abrir no celular dele.</p></div></div>`
        : html`
          <div class="panel">
            <div class="kv"><span>Vendido por</span><strong>${seller.firstName}</strong></div>
            <div class="kv"><span>${v?.meterUnit === 'HOURS' ? 'Horímetro na venda' : 'Km na venda'}</span><strong>${fmtKm(t.km, v?.meterUnit)}</strong></div>
            <div class="kv"><span>Histórico de manutenção</span><strong>${historyCount ? `${historyCount} ${historyCount === 1 ? 'registro' : 'registros'}` : 'sem registros'}</strong></div>
          </div>
          <h2 class="wiz-sub">Ao aceitar</h2>
          <ul class="steps-list check">
            <li>O veículo entra na sua garagem como <strong>dono provisório</strong>, com todo o histórico.</li>
            <li>Você recebe os lembretes de revisão e pode autorizar empresas.</li>
            <li>Quando o Detran emitir o CRLV-e no seu nome, envie pelo app para virar o dono.</li>
          </ul>
          <div class="wiz-foot">
            <ion-button expand="block" data-action="accept"><ion-icon slot="start" name="checkmark-circle-outline"></ion-icon>Aceitar e trazer para minha garagem</ion-button>
            <ion-button expand="block" fill="clear" color="medium" data-action="decline">Não é meu veículo / recusar</ion-button>
          </div>`}`.value;
  }

  async accept(button) {
    await busy(button, async () => {
      try {
        const r = await vehicles.acceptTransfer(this.token);
        toastSuccess('O veículo está na sua garagem.');
        go(`/vehicles/${r.vehicleId}`, 'root');
      } catch (e) {
        if (e.status === 410) return this.renderClosed(e.message);
        toastError(e.message);
      }
    });
  }

  async decline() {
    if (!(await confirm({ header: 'Recusar a transferência?', message: 'O vendedor é avisado. O veículo continua com ele.', confirmText: 'Recusar', cancelText: 'Voltar', destructive: true }))) return;
    try {
      await vehicles.declineTransfer(this.token);
      toast('Transferência recusada.');
      go('/home', 'root');
    } catch (e) {
      toastError(e.message);
    }
  }
}

customElements.define('page-transfer-accept', TransferAcceptPage);
